import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LockErrorList } from '@/accessControl/LockErrorList';
import { LockErrorSharedReasonSummary } from '@/accessControl/LockErrorSharedReasonSummary';
import { GLOBAL_BUCKET_KEY, getGroupSharedReason } from '@/accessControl/groupLockErrorsByWorkbook';
import { FakeLink } from '@/core/FakeLink';
import { getWorksheetLink, goToWithReload } from '@/main/routing.utilities';
import { faChevronDown, faChevronRight } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Accordion, Badge, Icon } from '@seeqdev/qomponents';
import type { WorkbookLockErrorGroup } from '@/accessControl/groupLockErrorsByWorkbook';

const ACCORDION_OPEN_VALUE = 'open';

/**

/**
 * One collapsible section of the Lock tab, grouping every lock-blocking error that originated from a single
 * workbook (or, for `GLOBAL_BUCKET_KEY`, the fallback bucket for items with no resolvable workbook). Each group gets
 * its own single-item `Accordion` instance - rather than one shared `Accordion` across every group - so that groups
 * expand/collapse independently instead of `Accordion`'s built-in `type="single"` behavior forcing only one group
 * open at a time.
 */
export const LockErrorWorkbookGroupSection = ({
  group,
  isVantage,
}: {
  group: WorkbookLockErrorGroup;
  isVantage: boolean;
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const { workbook, errors } = group;
  const isGlobalBucket = group.workbookId === GLOBAL_BUCKET_KEY;

  /**
   * When every error in this group failed for the same reason, the group says it once above a plain list of the
   * items, instead of repeating an identical sentence under each one. Otherwise the reasons differ and each item
   * keeps its own.
   */
  const sharedReason = getGroupSharedReason(group);

  const header = (
    <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:pl-sq-5">
      <div className="tw:flex tw:flex-row tw:items-center">
        <Icon icon={isExpanded ? faChevronDown : faChevronRight} extraClassNames="tw:mr-sq-7 tw:text-sq-text-muted" />
        {isGlobalBucket ? (
          <strong data-testid={`workbook-group-name-${group.workbookId}`}>
            {t('ACCESS_CONTROL.LOCKING.GROUPED_BY_WORKBOOK.ITEMS_OUTSIDE_WORKBOOKS')}
          </strong>
        ) : (
          <FakeLink
            onClick={(e) => {
              e.stopPropagation();
              goToWithReload(getWorksheetLink(workbook!.id));
            }}>
            <strong data-testid={`workbook-group-name-${group.workbookId}`}>{workbook!.name}</strong>
          </FakeLink>
        )}
        <Badge variant="neutral" extraClassNames="tw:ml-sq-7">
          {errors.length}
        </Badge>
      </div>
      {!isGlobalBucket ? (
        <Badge variant={workbook!.locked ? 'success' : 'error'}>
          {t(workbook!.locked ? 'ACCESS_CONTROL.LOCKING.LOCKED' : 'ACCESS_CONTROL.LOCKING.UNLOCKED')}
        </Badge>
      ) : null}
    </div>
  );

  return (
    <div className="tw:mb-sq-10" data-testid={`workbook-group-${group.workbookId}`}>
      <Accordion
        testId={`workbook-group-accordion-${group.workbookId}`}
        value={isExpanded ? ACCORDION_OPEN_VALUE : ''}
        onItemSelect={(value) => setIsExpanded(value === ACCORDION_OPEN_VALUE)}
        accordionItems={[
          {
            value: ACCORDION_OPEN_VALUE,
            trigger: header,
            content: sharedReason ? (
              <div className="tw:pt-sq-10 tw:pl-5">
                <LockErrorSharedReasonSummary
                  errors={errors}
                  sharedReason={sharedReason}
                  hasWorkbook={!isGlobalBucket}
                  isVantage={isVantage}
                  testId={`workbookGroupSummary-${group.workbookId}`}
                />
              </div>
            ) : (
              <div className="tw:pt-sq-10 tw:pl-5">
                <LockErrorList errors={errors} isVantage={isVantage} />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
